import { NavLink, Link } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { BRAND } from "../lib/content";

export default function Navbar() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home"), end: true, testId: "nav-home" },
    { to: "/chambres", label: t("nav.rooms"), testId: "nav-rooms" },
    { to: "/galerie", label: t("nav.gallery"), testId: "nav-gallery" },
    { to: "/grand-popo", label: t("nav.location"), testId: "nav-location" },
    { to: "/activites", label: t("nav.activities"), testId: "nav-activities" },
    { to: "/contact", label: t("nav.contact"), testId: "nav-contact" },
  ];

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-[#F9F6F0]/85 border-b border-[#6B4A2B]/10"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
        <NavLogo onClick={() => setOpen(false)} />

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-[#6B4A2B]"
                    : "text-[#2C241B]/75 hover:text-[#6B4A2B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <LangSwitcher />

          <Link
            to="/reserver"
            data-testid="nav-book-cta"
            className="hidden md:inline-flex btn-primary py-3 px-5"
          >
            {t("nav.book")}
          </Link>

          <button
            className="lg:hidden p-2 text-[#2C241B]"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && <MobileMenu links={links} onClose={() => setOpen(false)} />}
    </header>
  );
}

function NavLogo({ onClick }) {
  return (
    <Link
      to="/"
      className="flex items-center gap-3"
      data-testid="nav-logo"
      onClick={onClick}
    >
      <img
        src={BRAND.logo}
        alt="Guesthouse Bambou Beach"
        className="h-11 w-11 object-contain"
      />
      <div className="leading-tight hidden sm:block">
        <div className="font-serif-display text-[17px] text-[#2C241B]">
          Bambou Beach
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B4A2B]/80">
          Grand-Popo · Bénin
        </div>
      </div>
    </Link>
  );
}

function LangSwitcher() {
  const { lang, setLang } = useI18n();

  return (
    <div
      className="hidden md:flex items-center text-[11px] tracking-[0.22em] uppercase text-[#6B4A2B]"
      data-testid="language-switcher"
    >
      <button
        onClick={() => setLang("fr")}
        data-testid="lang-fr"
        className={`px-2 py-1 ${
          lang === "fr"
            ? "text-[#2C241B] font-semibold"
            : "opacity-60 hover:opacity-100"
        }`}
      >
        FR
      </button>
      <span className="opacity-40">/</span>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en"
        className={`px-2 py-1 ${
          lang === "en"
            ? "text-[#2C241B] font-semibold"
            : "opacity-60 hover:opacity-100"
        }`}
      >
        EN
      </button>
    </div>
  );
}

function MobileMenu({ links, onClose }) {
  const { t, lang, setLang } = useI18n();

  return (
    <div className="lg:hidden border-t border-[#6B4A2B]/10 bg-[#F9F6F0]" data-testid="mobile-menu">
      <div className="px-5 py-6 flex flex-col gap-5">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={onClose}
            data-testid={`mobile-${l.testId}`}
            className={({ isActive }) =>
              `text-sm tracking-wide ${
                isActive ? "text-[#6B4A2B] font-semibold" : "text-[#2C241B]/80"
              }`
            }
          >
            {l.label}
          </NavLink>
        ))}
        <Link
          to="/reserver"
          onClick={onClose}
          data-testid="mobile-nav-book-cta"
          className="btn-primary justify-center"
        >
          {t("nav.book")}
        </Link>
        <div className="flex items-center gap-3 text-xs tracking-[0.22em] uppercase text-[#6B4A2B] pt-2">
          <button
            onClick={() => setLang("fr")}
            data-testid="mobile-lang-fr"
            className={lang === "fr" ? "font-semibold text-[#2C241B]" : "opacity-60"}
          >
            FR
          </button>
          <span className="opacity-40">/</span>
          <button
            onClick={() => setLang("en")}
            data-testid="mobile-lang-en"
            className={lang === "en" ? "font-semibold text-[#2C241B]" : "opacity-60"}
          >
            EN
          </button>
        </div>
      </div>
    </div>
  );
}
